import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Target,
  Tag,
  Calendar,
  User,
  Building2,
  TrendingUp,
  Loader2,
  Plus,
  Users,
  Package,
  PackagePlus,
  ShoppingCart,
  X,
  ChevronDown,
  ChevronUp,
  Percent,
  Edit,
  Check,
  Trash2,
  List,
  Globe,
  Coins,
  FileText,
} from "lucide-react";
import { FileUploader, Attachment } from "@/components/shared/FileUploader";
import { useToast } from "@/hooks/use-toast";
import { usePipelinePermissions } from "@/hooks/usePipelinePermissions";
import {
  opportunityAPI,
  CreateOpportunityWithItemsData,
  CreateOpportunityItemData,
  OpportunityResponse,
} from "@/services/opportunityService";
import { opportunitySourcesAPI } from "@/services/opportunitySourcesService";
import { companyAPI } from "@/services/companyService";
import { contactoAPI, Contacto } from "@/services/contactoService";
import { OpportunityStage } from "@/services/opportunityService";
import { currencyAPI, Currency } from "@/services/currencyService";
import { CurrencyModal } from "./CurrencyModal";
import { usersService } from "@/services/usersService";
import type { User as SystemUser } from "@/types/crm";
import { productService, Product } from "@/services/product.service";
import { CreateContactModal } from "@/components/contacts/CreateContactModal";
import { CreateCompanyModal } from "@/components/companies/CreateCompanyModal";
import { OpportunitySourcesModal } from "./OpportunitySourcesModal";
import { useAuth } from "@/hooks/useAuth";
import { CreateRuleModal } from "@/components/products/CreateRuleModal";
import { Badge } from "@/components/ui/badge";
import { pipelineAPI } from "@/services/pipelineService";
import { AddProductModal } from "./AddProductModal";
import { separateRules } from "@/utils/ruleUtils";

interface CreateOpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  slug: string;
  pipelineId?: string;
  preloadedData?: {
    contacts?: Contacto[];
    companies?: any[];
    stages?: OpportunityStage[];
    sources?: any[];
    currencies?: Currency[];
    users?: SystemUser[];
  };
}

interface ProductWithSelection {
  product: Product;
  quantity: number;
  selectedRules: string[];
  appliedRules?: any[]; // Guardar objetos completos de reglas
  isManual?: boolean;
  manualName?: string;
  manualPrice?: string;
  basePrice?: number;
  notes?: string;
}

const DIALOG_DESCRIPTION_ID = "create-opportunity-description";

const formatCurrency = (value: number) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDisplayValue = (value: string | number) => {
  if (value === undefined || value === null) return "";
  const strValue = value.toString();
  if (!strValue) return "";
  const [integer, decimal] = strValue.split(".");
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal !== undefined
    ? `${formattedInteger}.${decimal}`
    : formattedInteger;
};

export function CreateOpportunityModal({
  isOpen,
  onClose,
  onSuccess,
  slug,
  pipelineId,
  preloadedData,
}: CreateOpportunityModalProps) {
  const { toast } = useToast();
  const { user, company } = useAuth();
  const { canCreateOpportunityInPipeline } = usePipelinePermissions();
  const isMounted = useRef(true);

  const [formData, setFormData] = useState({
    name: "",
    estimated_value: "0",
    success_probability: "50",
    stage_id: "",
    estimated_close_date: "",
    contact_id: "",
    client_company_id: "",
    opportunity_source_id: "",
    notes: "",
    priority: "Mediana",
    currency_id: "",
    created_by: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [discount, setDiscount] = useState({
    value: "0",
    type: "percentage" as "fixed" | "percentage",
  });

  const [showContactModal, setShowContactModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [selectedCollaboratorIds, setSelectedCollaboratorIds] = useState<
    string[]
  >([]);
  const [allowedRoleIds, setAllowedRoleIds] = useState<string[]>([]);

  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<
    ProductWithSelection[]
  >([]);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(
    null,
  );

  const [contacts, setContacts] = useState<Contacto[]>(
    preloadedData?.contacts || [],
  );
  const [companies, setCompanies] = useState<any[]>(
    preloadedData?.companies || [],
  );
  const [stages, setStages] = useState<OpportunityStage[]>(
    preloadedData?.stages || [],
  );
  const [sources, setSources] = useState<any[]>(preloadedData?.sources || []);
  const [currencies, setCurrencies] = useState<Currency[]>(
    preloadedData?.currencies || [],
  );
  const [users, setUsers] = useState<SystemUser[]>(preloadedData?.users || []);

  const [loading, setLoading] = useState({
    contacts: false,
    companies: false,
    stages: false,
    sources: false,
    currencies: false,
    users: false,
    products: false,
    submit: false,
  });

  const currencySymbol = useMemo(() => {
    if ((company as any)?.currency) return (company as any).currency;
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "Q";
  }, [company]);

  const uniqueUsers = useMemo(() => {
    if (!users || users.length === 0) return [];
    const userMap = new Map<string, SystemUser>();
    users.forEach((user) => {
      if (!userMap.has(user.id)) {
        userMap.set(user.id, user);
      }
    });
    return Array.from(userMap.values());
  }, [users]);

  const [hasLoadedInitialData, setHasLoadedInitialData] =
    useState(!!preloadedData);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const filteredStages = useMemo(() => {
    if (!pipelineId) return stages;
    return stages.filter((stage) => (stage as any).pipeline_id === pipelineId);
  }, [stages, pipelineId]);

  useEffect(() => {
    if (isOpen && user?.id) {
      setFormData((prev) => ({ ...prev, created_by: user.id }));
    }
  }, [isOpen, user?.id]);

  useEffect(() => {
    if (isOpen && slug) {
      productService
        .getProducts({ slug, is_active: true })
        .then((res) => {
          if (isMounted.current) {
            const activeProducts = (res.data || []).filter((p) => p.is_active);
            setProducts(activeProducts);
          }
        })
        .catch((err) => console.error("Error fetching products", err));
    }
  }, [isOpen, slug]);

  useEffect(() => {
    if (isOpen && slug && !hasLoadedInitialData) {
      fetchDataParallel();
    } else if (isOpen && hasLoadedInitialData) {
      setDefaultValues();
    }
  }, [isOpen, slug, hasLoadedInitialData]);

  const fetchDataParallel = async () => {
    if (!isMounted.current) return;
    try {
      const stagesParams: any = { slug, is_active: true };
      if (pipelineId) {
        stagesParams.pipeline_id = pipelineId;
      }

      const [
        contactsData,
        companiesResponse,
        stagesResponse,
        sourcesResponse,
        currenciesResponse,
        usersResponse,
        productsResponse,
      ] = await Promise.all([
        contactoAPI.getContactos(slug),
        companyAPI.getCompanies({ slug }),
        opportunityAPI.getStages(stagesParams),
        opportunitySourcesAPI.getSources({ slug, is_active: true }),
        currencyAPI.getCurrencies({ slug, is_active: true }),
        usersService.getUsers(slug, 1, 1000, true),
        productService.getProducts({ slug }),
      ]);

      if (isMounted.current) {
        setContacts(contactsData);
        setCompanies(companiesResponse.data);
        setStages(stagesResponse.data);
        setSources(sourcesResponse.data);
        setCurrencies(currenciesResponse.data);
        setUsers(usersResponse.data);
        setProducts(productsResponse.data || []);
        setHasLoadedInitialData(true);

        const defaultSource = sourcesResponse.data.find(
          (s: any) => s.is_default,
        );
        const defaultStage = stagesResponse.data.find(
          (s: any) => s.stage_type === "start",
        );
        const defaultCurrency = currenciesResponse.data.find(
          (c: Currency) => c.is_default,
        );

        setFormData((prev) => ({
          ...prev,
          stage_id: defaultStage?.id || stagesResponse.data[0]?.id || "",
          opportunity_source_id:
            defaultSource?.id || sourcesResponse.data[0]?.id || "",
          currency_id:
            defaultCurrency?.id || currenciesResponse.data[0]?.id || "",
        }));
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const setDefaultValues = () => {
    if (!hasLoadedInitialData || !isMounted.current) return;
    const defaultSource = sources.find((s: any) => s.is_default);
    const defaultStage = filteredStages.find(
      (s: any) => s.stage_type === "start",
    );
    const defaultCurrency = currencies.find((c: Currency) => c.is_default);

    setFormData((prev) => ({
      ...prev,
      stage_id:
        prev.stage_id || defaultStage?.id || filteredStages[0]?.id || "",
      opportunity_source_id:
        prev.opportunity_source_id || defaultSource?.id || sources[0]?.id || "",
      currency_id:
        prev.currency_id || defaultCurrency?.id || currencies[0]?.id || "",
    }));
  };

  const resetForm = useCallback(() => {
    if (!isMounted.current) return;
    setFormData({
      name: "",
      estimated_value: "0",
      success_probability: "50",
      stage_id: "",
      estimated_close_date: "",
      contact_id: "",
      client_company_id: "",
      opportunity_source_id: "",
      notes: "",
      priority: "Mediana",
      currency_id: "",
      created_by: "",
    });
    setErrors({});
    setAttachments([]);
    setSelectedProducts([]);
    setShowAddProductModal(false);

    setDiscount({ value: "0", type: "percentage" });
    setSelectedCollaboratorIds([]);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = () => {
    onClose();
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (value.trim()) delete newErrors[field];
      return newErrors;
    });
  };

  const handleCurrencyInputChange = (
    field: keyof typeof formData,
    value: string,
  ) => {
    const rawValue = value.replace(/,/g, "");
    const sanitizedValue = rawValue.replace(/[^0-9.]/g, "");
    handleChange(field, sanitizedValue);
  };

  const handleDiscountChange = (value: string) => {
    const rawValue = value.replace(/,/g, "");
    const sanitizedValue = rawValue.replace(/[^0-9.]/g, "");
    setDiscount((prev) => ({ ...prev, value: sanitizedValue }));
  };

  const calculateDetailedTotals = useCallback(() => {
    let baseSubtotal = 0;
    let extrasSubtotal = 0;

    selectedProducts.forEach((item: any) => {
      const { quantity, isManual, manualPrice, basePrice } = item;
      let numericBasePrice = isManual
        ? parseFloat(manualPrice || "0")
        : basePrice !== undefined
          ? basePrice
          : typeof item.product.base_price === "string"
            ? parseFloat(item.product.base_price)
            : item.product.base_price;

      let itemBaseTotal = numericBasePrice * quantity;
      baseSubtotal += itemBaseTotal;

      if (!isManual && item.appliedRules) {
        item.appliedRules.forEach((rule: any) => {
          const modifier =
            typeof rule.price_modifier === "string"
              ? parseFloat(rule.price_modifier)
              : rule.price_modifier || 0;
          if (rule.price_type === "percentage") {
            extrasSubtotal += (itemBaseTotal * modifier) / 100;
          } else if (rule.price_type === "per_unit") {
            extrasSubtotal += modifier * quantity;
          } else {
            extrasSubtotal += modifier;
          }
        });
      }
    });

    const subtotal = baseSubtotal + extrasSubtotal;
    let discountAmount = 0;
    const discountVal = parseFloat(discount.value) || 0;
    if (discount.type === "percentage") {
      discountAmount = (subtotal * discountVal) / 100;
    } else {
      discountAmount = discountVal;
    }
    const total = Math.max(0, subtotal - discountAmount);

    return { baseSubtotal, extrasSubtotal, subtotal, discountAmount, total };
  }, [selectedProducts, discount]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      const { total } = calculateDetailedTotals();
      setFormData((prev) => ({
        ...prev,
        estimated_value: total.toString(),
      }));
    }
  }, [selectedProducts, discount, calculateDetailedTotals]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting opportunity...');
    // Validación básica
    if (!formData.name) {
      toast({
        title: "Error",
        description: "El nombre es obligatorio",
        variant: "destructive",
      });
      return;
    }

    setLoading((prev) => ({ ...prev, submit: true }));
    try {
      const finalNotes =
        discount.value !== "0"
          ? `${formData.notes}\n\n[DISCOUNT:${JSON.stringify(discount)}]`
          : formData.notes;

      console.log('Final notes to send:', finalNotes);
      console.log('Selected products before payload:', selectedProducts);

      const payload: any = {
        ...formData,
        opportunity_source_id: formData.opportunity_source_id || null,
        contact_id: formData.contact_id || null,
        client_company_id: formData.client_company_id || null,
        currency_id: formData.currency_id || null,
        notes: finalNotes,
        slug,
        estimated_value:
          selectedProducts.length > 0
            ? calculateDetailedTotals().total
            : parseFloat(formData.estimated_value),
        collaborators: selectedCollaboratorIds.map((id) => ({ user_id: id })),
        items:
          selectedProducts.length > 0
            ? selectedProducts.map((p) => ({
                product_id: p.isManual ? null : p.product.id,
                description: p.isManual ? p.manualName : p.product.name,
                notes: p.notes || "",
                quantity: p.quantity,
                base_price: p.isManual
                  ? parseFloat(p.manualPrice || "0")
                  : p.basePrice !== undefined
                    ? p.basePrice
                    : p.product.base_price,
                applied_rules:
                  p.appliedRules?.map((r) => ({
                    rule_id: r.rule_id,
                    value: "1",
                    is_custom: r.is_custom,
                    rule_name: r.rule_name,
                    description: r.description,
                    price_modifier: r.price_modifier,
                    price_type: r.price_type,
                  })) || [],
              }))
            : [],
      };

      console.log('Creating opportunity with payload:', payload);

      const response = await opportunityAPI.createOpportunity(payload);

      console.log('Opportunity creation response:', response);

      // 3. Upload attachments if any in parallel
      if (attachments && attachments.length > 0 && response.data?.id) {
        console.log(
          `Subiendo ${attachments.length} archivos para la nueva oportunidad ${response.data.id} en paralelo`,
        );
        await Promise.all(
          attachments.map(async (att) => {
            try {
              await opportunityAPI.uploadAttachment(
                response.data.id,
                slug,
                att.file,
                att.description || "Archivo adjunto",
              );
            } catch (err) {
              console.error("Error al subir archivo adjunto:", err);
              toast({
                title: "Error parcial",
                description: `Oportunidad creada, pero no se pudo subir el archivo ${att.file.name}`,
                variant: "destructive",
              });
            }
          }),
        );
      }

      toast({
        title: "Éxito",
        description: "Oportunidad creada correctamente",
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al crear",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  const handleAddProductFromModal = (productData: any) => {
    const isManual = productData.isManual;
    const productObj = isManual
      ? null
      : products.find((p) => p.id === productData.product_id);

    if (!isManual && !productObj) {
      toast({
        title: "Error",
        description: "Producto no encontrado",
        variant: "destructive",
      });
      return;
    }

    if (editingProductIndex !== null) {
      const updated = [...selectedProducts];
      updated[editingProductIndex] = {
        ...updated[editingProductIndex],
        isManual,
        manualName: productData.manualName,
        manualPrice: productData.manualPrice,
        product: productObj,
        quantity: productData.quantity,
        appliedRules: productData.applied_rules,
        basePrice: productData.base_price,
        notes: productData.notes || "",
      };
      setSelectedProducts(updated);
      setEditingProductIndex(null);
    } else {
      setSelectedProducts([
        ...selectedProducts,
        {
          isManual,
          manualName: productData.manualName,
          manualPrice: productData.manualPrice,
          product: productObj,
          quantity: productData.quantity,
          selectedRules:
            productData.applied_rules?.map((r: any) => r.rule_id) || [],
          appliedRules: productData.applied_rules,
          basePrice: productData.base_price,
          notes: productData.notes || "",
        },
      ]);
    }
    setShowAddProductModal(false);
  };

  const getUserDisplayName = (user: SystemUser): string => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim();
    }
    return user.nombre || user.name || "";
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          {loading.submit && (
            <div className="absolute inset-0 z-50 bg-white/70 backdrop-blur-[1px] flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm font-semibold text-slate-600">
                Guardando oportunidad...
              </p>
            </div>
          )}
          <div className="flex flex-col h-full max-h-[90vh]">
            <DialogHeader className="p-6 pb-2">
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <PackagePlus className="h-5 w-5 text-green-600" />
                Crear Nueva Oportunidad
              </DialogTitle>
              <DialogDescription id={DIALOG_DESCRIPTION_ID}>
                Completa los datos para iniciar el seguimiento de una nueva
                venta.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto p-6 pt-2 custom-scrollbar">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="name" className="flex items-center">
                      <Target className="h-4 w-4 mr-2" />
                      Nombre de la Oportunidad *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Ej: Venta de Software Kersales"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="estimated_value"
                      className="flex items-center"
                    >
                      <Coins className="h-4 w-4 mr-2" />
                      Valor Estimado
                    </Label>
                    <div className="relative mt-1">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <span className="text-gray-500 font-medium">
                          {currencySymbol}
                        </span>
                      </div>
                      <Input
                        id="estimated_value"
                        value={formatDisplayValue(formData.estimated_value)}
                        onChange={(e) =>
                          handleCurrencyInputChange(
                            "estimated_value",
                            e.target.value,
                          )
                        }
                        className="pl-10"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="success_probability"
                      className="flex items-center"
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Probabilidad (%)
                    </Label>
                    <Select
                      onValueChange={(val) =>
                        handleChange("success_probability", val)
                      }
                      value={formData.success_probability}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10% - Muy baja</SelectItem>
                        <SelectItem value="25">25% - Baja</SelectItem>
                        <SelectItem value="50">50% - Media</SelectItem>
                        <SelectItem value="75">75% - Alta</SelectItem>
                        <SelectItem value="90">90% - Muy alta</SelectItem>
                        <SelectItem value="100">
                          100% - Cerrada ganada
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="stage_id" className="flex items-center">
                      <List className="h-4 w-4 mr-2" />
                      Etapa *
                    </Label>
                    <Select
                      value={formData.stage_id}
                      onValueChange={(val) => handleChange("stage_id", val)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleccionar etapa" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredStages.map((stage) => (
                          <SelectItem key={stage.id} value={stage.id}>
                            <div className="flex items-center gap-2">
                              {stage.color && (
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: stage.color }}
                                />
                              )}
                              {stage.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label
                      htmlFor="estimated_close_date"
                      className="flex items-center"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Cierre Estimado
                    </Label>
                    <Input
                      type="date"
                      id="estimated_close_date"
                      value={formData.estimated_close_date}
                      onChange={(e) =>
                        handleChange("estimated_close_date", e.target.value)
                      }
                      className="mt-1"
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="client_company_id"
                      className="flex items-center"
                    >
                      <Building2 className="h-4 w-4 mr-2" />
                      Empresa *
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        value={formData.client_company_id}
                        onValueChange={(val) => {
                          handleChange("client_company_id", val);
                          handleChange("contact_id", "");
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Empresa" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowCompanyModal(true)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="contact_id" className="flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      Contacto *
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        value={formData.contact_id}
                        onValueChange={(val) => handleChange("contact_id", val)}
                        disabled={!formData.client_company_id}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue
                            placeholder={
                              formData.client_company_id
                                ? "Seleccionar contacto"
                                : "Selecciona una empresa primero"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {contacts
                            .filter(
                              (contact) =>
                                contact.client_company_id ===
                                formData.client_company_id,
                            )
                            .map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {contact.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowContactModal(true)}
                        disabled={!formData.client_company_id}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="opportunity_source_id"
                      className="flex items-center"
                    >
                      <Globe className="h-4 w-4 mr-2" />
                      Fuente
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        value={formData.opportunity_source_id}
                        onValueChange={(val) =>
                          handleChange("opportunity_source_id", val)
                        }
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Fuente" />
                        </SelectTrigger>
                        <SelectContent>
                          {sources.map((source) => (
                            <SelectItem key={source.id} value={source.id}>
                              {source.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowSourceModal(true)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Agregar en Copia
                    </Label>
                    <Select
                      onValueChange={(val) => {
                        if (!selectedCollaboratorIds.includes(val)) {
                          setSelectedCollaboratorIds((prev) => [...prev, val]);
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Añadir..." />
                      </SelectTrigger>
                      <SelectContent>
                        {uniqueUsers
                          .filter(
                            (u) =>
                              !selectedCollaboratorIds.includes(u.id) &&
                              u.id !== user?.id,
                          )
                          .map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {getUserDisplayName(u)}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedCollaboratorIds.map((id) => {
                        const u = users.find((user) => user.id === id);
                        return (
                          <Badge
                            key={id}
                            variant="secondary"
                            className="gap-1 pr-1"
                          >
                            {u ? getUserDisplayName(u) : id}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() =>
                                setSelectedCollaboratorIds((prev) =>
                                  prev.filter((uid) => uid !== id),
                                )
                              }
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="notes" className="flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Notas / Próximo Paso
                    </Label>
                    <Input
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleChange("notes", e.target.value)}
                      placeholder="Detalles adicionales..."
                      className="mt-1"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <FileUploader
                      attachments={attachments}
                      onAttachmentsChange={setAttachments}
                      maxFiles={5}
                      label="Adjuntos"
                    />
                  </div>
                </div>
                <div className="md:col-span-2 space-y-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">
                      Listado de Productos
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingProductIndex(null);
                        setShowAddProductModal(true);
                      }}
                      className="bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Añadir Producto
                    </Button>
                  </div>

                  {selectedProducts.length > 0 && (
                    <div className="space-y-2">
                      {selectedProducts.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg group hover:border-blue-300 transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-md">
                              <Package className="h-4 w-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold">
                                {item.isManual
                                  ? item.manualName
                                  : item.product.name}
                              </p>
                              <div className="mt-1 w-full">
                                {(() => {
                                  const isManual = item.isManual;
                                  const catalogPrice = isManual
                                    ? parseFloat(item.manualPrice || "0")
                                    : parseFloat(
                                        item.product?.base_price?.toString() ||
                                          "0",
                                      );
                                  const isModified =
                                    !isManual &&
                                    item.basePrice !== undefined &&
                                    Math.abs(item.basePrice - catalogPrice) >
                                      0.01;
                                  const numericBasePrice = isModified
                                    ? item.basePrice
                                    : catalogPrice;

                                  const baseSubtotal =
                                    numericBasePrice * item.quantity;

                                  let extrasTotal = 0;
                                  const customRulesOnly =
                                    item.appliedRules?.filter(
                                      (r: any) => !r.is_required,
                                    ) || [];
                                  const customCount = customRulesOnly.length;
                                  let customExtrasTotal = 0;
                                  const rulesCount =
                                    item.appliedRules?.length || 0;
                                  if (!isManual && rulesCount > 0) {
                                    item.appliedRules.forEach((rule: any) => {
                                      const modifier =
                                        typeof rule.price_modifier === "string"
                                          ? parseFloat(rule.price_modifier)
                                          : rule.price_modifier || 0;
                                      let ruleTotal = 0;
                                      if (rule.price_type === "percentage") {
                                        ruleTotal =
                                          baseSubtotal * (modifier / 100);
                                      } else if (
                                        rule.price_type === "per_unit"
                                      ) {
                                        ruleTotal = modifier * item.quantity;
                                      } else {
                                        ruleTotal = modifier;
                                      }
                                      extrasTotal += ruleTotal;
                                      if (!rule.is_required)
                                        customExtrasTotal += ruleTotal;
                                    });
                                  }

                                  const lineTotal = baseSubtotal + extrasTotal;

                                  return (
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                                      <span>
                                        <span className="font-bold text-slate-700">
                                          Cant:
                                        </span>{" "}
                                        {item.quantity}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <span className="font-bold text-slate-700">
                                          P. Base:
                                        </span>
                                        {isModified && (
                                          <span className="line-through text-slate-400">
                                            {currencySymbol}
                                            {formatCurrency(catalogPrice)}
                                          </span>
                                        )}
                                        <span
                                          className={`${isModified ? "text-orange-600 font-bold" : ""}`}
                                        >
                                          {currencySymbol}
                                          {formatCurrency(numericBasePrice)}
                                          {isModified && " (Modif.)"}
                                        </span>
                                      </span>
                                      {customCount > 0 && (
                                        <span className="text-blue-600 font-bold bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 flex items-center">
                                          {customCount}{" "}
                                          {customExtrasTotal < 0
                                            ? customCount === 1
                                              ? "Descuento"
                                              : "Descuentos"
                                            : customCount === 1
                                              ? "Servicio"
                                              : "Servicios"}{" "}
                                          ({customExtrasTotal >= 0 ? "+" : "-"}
                                          {currencySymbol}
                                          {formatCurrency(
                                            Math.abs(customExtrasTotal),
                                          )}
                                          )
                                        </span>
                                      )}
                                      <span className="font-bold text-slate-800 ml-auto bg-slate-100 px-2 py-0.5 rounded">
                                        Total: {currencySymbol}
                                        {formatCurrency(lineTotal)}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Seccion de Notas y Servicios */}
                              {(item.notes ||
                                (item.appliedRules &&
                                  item.appliedRules.length > 0)) && (
                                <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-1">
                                  {item.notes && (
                                    <p className="text-[10px] text-slate-600">
                                      <span className="font-semibold text-slate-700">
                                        Notas:
                                      </span>{" "}
                                      {item.notes}
                                    </p>
                                  )}
                                  {(() => {
                                    const { required, custom } = separateRules(
                                      item.appliedRules || [],
                                    );
                                    return (
                                      <>
                                        {required.length > 0 && (
                                          <div className="mt-1">
                                            <span className="font-semibold text-[11px] text-rose-700 block mb-1">
                                              Ajustes Obligatorios:
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                              {required.map((r, idx) => (
                                                <Badge
                                                  key={`req-${idx}`}
                                                  variant="destructive"
                                                  className="text-[10px] py-0 px-1.5 h-4 bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                                                >
                                                  {r.rule_name || "Ajuste"}{" "}
                                                  {r.calculated_amount
                                                    ? `(${parseFloat(r.calculated_amount) >= 0 ? "+" : ""}${r.calculated_amount})`
                                                    : ""}
                                                </Badge>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {custom.length > 0 && (
                                          <div className="mt-1">
                                            <span className="font-semibold text-[11px] text-blue-700 block mb-1">
                                              Servicios / Extras:
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                              {custom.map((r, idx) => (
                                                <Badge
                                                  key={`cust-${idx}`}
                                                  variant="secondary"
                                                  className="text-[10px] py-0 px-1.5 h-4 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                                                >
                                                  {r.rule_name || "Servicio"}{" "}
                                                  {r.description
                                                    ? `- ${r.description}`
                                                    : ""}{" "}
                                                  {r.calculated_amount
                                                    ? `(${parseFloat(r.calculated_amount) >= 0 ? "+" : ""}${r.calculated_amount})`
                                                    : ""}
                                                </Badge>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!item.isManual && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-blue-600"
                                onClick={() => {
                                  setEditingProductIndex(index);
                                  setShowAddProductModal(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-rose-600"
                              onClick={() =>
                                setSelectedProducts(
                                  selectedProducts.filter(
                                    (_, i) => i !== index,
                                  ),
                                )
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedProducts.length > 0 && (
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <div className="flex flex-col md:flex-row justify-between gap-6">
                        <div className="space-y-3">
                          <Label className="text-xs font-bold text-slate-500 uppercase">
                            Descuento Global
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              value={formatDisplayValue(discount.value)}
                              onChange={(e) =>
                                handleDiscountChange(e.target.value)
                              }
                              className="w-24 h-9 bg-white"
                              placeholder="0.00"
                            />
                            <div className="flex bg-white rounded-md p-0.5 border border-slate-200">
                              <button
                                type="button"
                                onClick={() =>
                                  setDiscount({
                                    ...discount,
                                    type: "percentage",
                                  })
                                }
                                className={`p-1.5 rounded-md ${discount.type === "percentage" ? "bg-slate-900 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
                              >
                                <Percent className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setDiscount({ ...discount, type: "fixed" })
                                }
                                className={`p-1.5 rounded-md ${discount.type === "fixed" ? "bg-slate-900 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
                              >
                                <Tag className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="w-full md:w-64 space-y-2">
                          {(() => {
                            const totals = calculateDetailedTotals();
                            return (
                              <>
                                <div className="space-y-1.5 pb-3 border-b border-slate-200 mb-2 max-h-40 overflow-y-auto custom-scrollbar pr-1">
                                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                                    Resumen de Ítems
                                  </p>
                                  {selectedProducts.map((p, i) => {
                                    const isManual = p.isManual;
                                    const numericBasePrice = isManual
                                      ? parseFloat(p.manualPrice || "0")
                                      : p.basePrice !== undefined
                                        ? p.basePrice
                                        : parseFloat(
                                            p.product?.base_price?.toString() ||
                                              "0",
                                          );
                                    let extrasTotal = 0;
                                    if (!isManual && p.appliedRules) {
                                      p.appliedRules.forEach((r: any) => {
                                        const mod =
                                          typeof r.price_modifier === "string"
                                            ? parseFloat(r.price_modifier)
                                            : r.price_modifier || 0;
                                        if (r.price_type === "percentage")
                                          extrasTotal +=
                                            (numericBasePrice *
                                              p.quantity *
                                              mod) /
                                            100;
                                        else if (r.price_type === "per_unit")
                                          extrasTotal += mod * p.quantity;
                                        else extrasTotal += mod;
                                      });
                                    }
                                    const lineTotal =
                                      numericBasePrice * p.quantity +
                                      extrasTotal;
                                    const name = isManual
                                      ? p.manualName
                                      : p.product.name;
                                    return (
                                      <div
                                        key={i}
                                        className="flex justify-between items-start text-[11px] text-slate-600 gap-2"
                                      >
                                        <span
                                          className="truncate flex-1"
                                          title={name}
                                        >
                                          {p.quantity}x {name}
                                        </span>
                                        <span className="font-medium">
                                          {currencySymbol}
                                          {formatCurrency(lineTotal)}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
                                  <span>Subtotal</span>
                                  <span>
                                    {currencySymbol}
                                    {formatCurrency(totals.subtotal)}
                                  </span>
                                </div>
                                {totals.discountAmount > 0 && (
                                  <div className="flex justify-between items-center text-xs text-red-600">
                                    <span>Descuento Global</span>
                                    <span>
                                      -{currencySymbol}
                                      {formatCurrency(totals.discountAmount)}
                                    </span>
                                  </div>
                                )}
                                <div className="flex justify-between items-center pt-2 border-t border-slate-200 mt-2">
                                  <span className="text-sm font-bold text-slate-900">
                                    Total Estimado
                                  </span>
                                  <span className="text-xl font-bold text-green-700">
                                    {formatCurrency(totals.total)}
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter className="pt-6 border-t border-slate-100 flex items-center justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={loading.submit}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading.submit}
                    className="bg-slate-900 hover:bg-black text-white px-8"
                  >
                    {loading.submit ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Crear Oportunidad
                  </Button>
                </DialogFooter>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AddProductModal
        isOpen={showAddProductModal}
        onClose={() => {
          setShowAddProductModal(false);
          setEditingProductIndex(null);
        }}
        onAddProduct={handleAddProductFromModal}
        allProducts={products}
        slug={slug}
        onRefreshProducts={() => {
          productService.getProducts({ slug }).then((res) => {
            if (isMounted.current) setProducts(res.data || []);
          });
        }}
        initialData={
          editingProductIndex !== null
            ? {
                product_id: selectedProducts[editingProductIndex].product.id,
                quantity: selectedProducts[editingProductIndex].quantity,
                notes: selectedProducts[editingProductIndex].notes || "",
                base_price:
                  selectedProducts[editingProductIndex].basePrice || 0,
                applied_rules:
                  selectedProducts[editingProductIndex].appliedRules?.map(
                    (r) => ({
                      rule_id: r.rule_id,
                      rule_name: r.rule_name,
                      is_custom: r.is_custom,
                      price_modifier: r.price_modifier,
                      price_type: r.price_type,
                      description: r.description,
                      value: "1",
                    }),
                  ) || [],
              }
            : null
        }
      />

      <CreateCompanyModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        companySlug={slug}
        onSave={async (newCompanyData) => {
          try {
            const response = await companyAPI.createCompany(newCompanyData);
            if (response.data) {
              setCompanies((prev) => [...prev, response.data]);
              setFormData((prev) => ({
                ...prev,
                client_company_id: response.data.id,
              }));
            }
          } catch (error) {
            console.error(error);
          }
        }}
      />

      <CreateContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        defaultCompanyId={formData.client_company_id || undefined}
        defaultCompanyName={
          companies.find((c) => c.id === formData.client_company_id)?.name ||
          undefined
        }
        onSave={async (newContact) => {
          try {
            const response = await contactoAPI.createContacto(slug, newContact);
            setContacts((prev) => [...prev, response]);
            setFormData((prev) => ({ ...prev, contact_id: response.id }));
          } catch (error) {
            console.error(error);
          }
        }}
      />

      <OpportunitySourcesModal
        isOpen={showSourceModal}
        onClose={() => setShowSourceModal(false)}
        slug={slug}
        onSourceCreated={fetchDataParallel}
      />
    </>
  );
}
